import { useBreadcrumbs } from "#src/Routers/breadcrumbsHelper";
import { useNavigate, useParams } from "#src/Routers/hooks";
import {
  FormContextProvider,
  useFormContext,
} from "#src/contexts/FormSchemaContext";
import { useBlockNavigation } from "#src/hooks/useBlockNavigation";
import {
  MultiStepFormProvider,
  useMultiStepFormContext,
} from "#src/hooks/useMultiStepForm";
import { FORMS_BREADCRUMB } from "#src/routes/forms";
import { FORMS_CATEGORIES_BREADCRUMB } from "#src/routes/forms/categories";
import {
  FORM_CATEGORY_DETAILS_BREADCRUMB,
  linkToFormCategoryDetails,
} from "#src/routes/forms/categories/[categoryId]";
import { CREATE_FORM_CATEGORY_FORM_TEMPLATE_BREADCRUMB } from "#src/routes/forms/categories/[categoryId]/create-form-template";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, Page, Pill, useToast } from "@validereinc/common-components";
import {
  FormCategoryAdapter,
  FormSchemaAdapter,
  FormSchemaQuestionDisplayType,
  FormSchemaType,
  Resources,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import { produce } from "immer";
import React, { useState } from "react";
import { EDIT_FORM_CATEGORY_FORM_TEMPLATE_BREADCRUMB } from "../edit-form-template/[formSchemaId]";
import { EditFormTemplateDialog } from "../edit-form-template/[formSchemaId]/EditFormTemplateDialog";
import { linkToFormTemplateDetail } from "../templates/[formTemplateId]";
import styles from "./CreateFormTemplate.module.scss";
import { NavigateWithoutSavingFormTemplateDialog } from "./NavigateWithoutSavingFormTemplateDialog";
import {
  CreateFormTemplateDetailsStep,
  CreateFormTemplateSectionStep,
} from "./steps";

const cx = classNames.bind(styles);

type FormValuesType = FormSchemaType & {
  config: {
    questions: Record<string, FormSchemaQuestionDisplayType>;
    sections: FormSchemaType["config"]["sections"];
  };
  should_restrict_form_submissions?: boolean;
};

export const CreateFormTemplatePageContent = () => {
  const {
    schema: { data: templateToEdit },
  } = useFormContext();
  const { categoryId, schemaId } = useParams<{
    categoryId: string;
    schemaId: string;
  }>();

  const { stepper, footer } = useMultiStepFormContext();

  const categoryQuery = useQuery({
    queryKey: [Resources.FORM_CATEGORY, categoryId],
    queryFn: ({ queryKey: [_, id] }) =>
      FormCategoryAdapter.getOne({
        id,
      }),
  });

  const breadcrumbs = useBreadcrumbs(
    [
      FORMS_BREADCRUMB,
      FORMS_CATEGORIES_BREADCRUMB,
      FORM_CATEGORY_DETAILS_BREADCRUMB,
      schemaId
        ? EDIT_FORM_CATEGORY_FORM_TEMPLATE_BREADCRUMB
        : CREATE_FORM_CATEGORY_FORM_TEMPLATE_BREADCRUMB,
    ],
    {
      2: categoryQuery?.data?.name,
    }
  );

  return (
    <Page
      breadcrumbs={breadcrumbs}
      category={schemaId ? "Edit Template" : categoryQuery?.data?.name}
      title={
        schemaId ? (
          <div className={cx("page-title")}>
            {templateToEdit?.name}
            {templateToEdit?.version && (
              <Pill variant="default">{`v${templateToEdit.version}`}</Pill>
            )}
          </div>
        ) : (
          "Create Template"
        )
      }
      actionRow={stepper}
      footer={footer}
    >
      <CreateFormTemplateDetailsStep />
      <CreateFormTemplateSectionStep />
    </Page>
  );
};

export const CreateFormTemplatePage = () => {
  const { toast } = useToast();
  const { categoryId, schemaId } = useParams<{
    categoryId: string;
    schemaId: string;
  }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [showNavigateAwayDialog, setShowNavigateAwayDialog] =
    useState<boolean>(false);
  const [confirmNavigateAway, setConfirmNavigateAway] =
    useState<boolean>(false);
  const [editedFormValues, setEditedFormValues] = useState<any[]>([]);

  const [, unblockNavigation] = useBlockNavigation(!confirmNavigateAway, () =>
    setShowNavigateAwayDialog(true)
  );

  const sanitizeFormValues = (formValues: FormValuesType) => {
    return produce(formValues, (draft: FormValuesType) => {
      delete draft.should_restrict_form_submissions;
      Object.values(draft.config.questions).forEach((question) => {
        if ("is_question_in_question_preview" in question) {
          delete question.is_question_in_question_preview;
        }
        if ("is_question_submission_deduplication_key" in question) {
          delete question.is_question_submission_deduplication_key;
        }
      });
    });
  };

  const createMutation = useMutation({
    // @ts-expect-error mismatch between return type and what's actually return in onSuccess, missing nested dat field in onSuccess
    mutationFn: async (formValues: FormValuesType) => {
      let submissionRestrictedRoleIds: string[] = [];
      if (formValues.should_restrict_form_submissions) {
        submissionRestrictedRoleIds =
          formValues.form_submission_restricted_role_ids;
      }

      const sanitizedFormValues = sanitizeFormValues(formValues);

      return FormSchemaAdapter.createOne({
        data: {
          ...sanitizedFormValues,
          form_submission_restricted_role_ids: submissionRestrictedRoleIds,
        },
      });
    },
    onSuccess: ({ data: { id } }) => {
      unblockNavigation({ redirect: false });

      toast.push({
        intent: "success",
        description: (
          <>
            Successfully created form template.{" "}
            <Link
              label="View Details"
              underline="always"
              onClick={() =>
                navigate({
                  pathname: linkToFormTemplateDetail(categoryId, id),
                })
              }
            />
          </>
        ),
      });

      queryClient.invalidateQueries({
        queryKey: [Resources.FORM_CATEGORY, categoryId],
      });

      queryClient.invalidateQueries({
        queryKey: [Resources.FORM_SCHEMA],
      });

      navigate({
        pathname: linkToFormCategoryDetails(categoryId),
      });
    },
    onError: (error: any) => {
      toast.push({
        intent: "error",
        // message in format of "config -> ... -> field_that_caused_error_message"
        description: `Failed to create form template. There's an issue with the following field: ${error.responseBodyJson?.error?.split("->").at(-1)}`,
      });
    },
    enabled: !schemaId,
  });

  const updateMutation = useMutation({
    // @ts-expect-error mismatch between return type and what's actually return in onSuccess, missing nested dat field in onSuccess
    mutationFn: async (formValues: FormValuesType) => {
      let submissionRestrictedRoleIds: string[] = [];
      if (formValues.should_restrict_form_submissions) {
        submissionRestrictedRoleIds =
          formValues.form_submission_restricted_role_ids;
      }

      const sanitizedFormValues = sanitizeFormValues(formValues);
      return FormSchemaAdapter.updateOne({
        id: schemaId,
        data: {
          ...sanitizedFormValues,
          form_submission_restricted_role_ids: submissionRestrictedRoleIds,
        },
      });
    },
    onSuccess: ({ data: { id, form_category_id } }) => {
      unblockNavigation({ redirect: false });

      toast.push({
        intent: "success",
        description: "Successfully saved a new version of the form template.",
      });
      queryClient.invalidateQueries({
        queryKey: [Resources.FORM_SCHEMA],
      });

      navigate({
        pathname: linkToFormTemplateDetail(form_category_id, id),
      });
    },
    onError: (error: any) => {
      toast.push({
        intent: "error",
        // message in format of "config -> ... -> field_that_caused_error_message"
        description: `Failed to save a new version of the form template. There's an issue with the following field: ${error.responseBodyJson?.error?.split("->").at(-1)}`,
      });
    },
    enabled: !!schemaId,
  });

  const onSubmitCreateTemplate = (values: any[]) => {
    createMutation.mutate({
      ...values.reduce((total, current) => ({ ...total, ...current }), {}),
      form_category_id: categoryId,
    });
  };
  const onSubmitEditTemplate = (values: any[]) => {
    updateMutation.mutate({
      ...values.reduce((total, current) => ({ ...total, ...current }), {}),
    });
  };

  const handleEditFormTemplate = (values: any[]) => {
    setEditedFormValues(values);
  };

  const onCloseEditFormTemplateDialog = () => {
    setEditedFormValues([]);
  };

  const onCancel = () => {
    navigate.goBack();
  };

  const handleConfirmNavigateAway = () => {
    setConfirmNavigateAway(true);
    onCloseConfirmNavigateAwayDialog();
    unblockNavigation({ redirect: true });
  };

  const onCloseConfirmNavigateAwayDialog = () => {
    setShowNavigateAwayDialog(false);
  };

  return (
    <FormContextProvider>
      <MultiStepFormProvider
        steps={[
          {
            label: "Template Details",
          },
          {
            label: "Sections & Questions",
          },
        ]}
        submissionActionText={schemaId ? "Save & Exit" : "Create"}
        onSubmit={schemaId ? handleEditFormTemplate : onSubmitCreateTemplate}
        onCancel={onCancel}
      >
        <CreateFormTemplatePageContent />
        <EditFormTemplateDialog
          isOpen={editedFormValues.length > 0}
          editedFormValues={editedFormValues}
          onSubmitEditTemplate={onSubmitEditTemplate}
          onClose={onCloseEditFormTemplateDialog}
        />
        <NavigateWithoutSavingFormTemplateDialog
          isOpen={showNavigateAwayDialog}
          handleConfirmNavigateAway={handleConfirmNavigateAway}
          onClose={onCloseConfirmNavigateAwayDialog}
          isCreating={!schemaId}
        />
      </MultiStepFormProvider>
    </FormContextProvider>
  );
};
